import { z } from 'zod';
// Request schemas for saved views (ADR-057) — per-user named serializations of a
// surface's URL view-state, ONE generic store shared by every surface
// (`/api/projects/:projectId/saved-views`). Rows are personal: every route
// operates on the caller's own rows under project membership — no capability key.
//
// `snapshot` is OPAQUE by design: the store never knows a surface's keys (columns
// join the definition later — the display block — without a wire break). It is
// validated only as a JSON object with a serialized-size cap; the consuming page
// filters unknown keys on apply (tolerant both directions).
/** The owning surface — `'sites'` today; later `'assets'`, `'report:<id>'` (ADR-057). */
export const SavedViewSurfaceKeySchema = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9:_-]*$/, 'lowercase key: a-z 0-9 : _ -');
// The serialized-size cap: a URL view-state object is ~20 short keys; 8 KB is
// generous headroom (the display block will grow it deliberately, not by drift).
export const SNAPSHOT_MAX_BYTES = 8 * 1024;
export const SavedViewSnapshotSchema = z
    .record(z.string().max(64), z.unknown())
    .refine((s) => JSON.stringify(s).length <= SNAPSHOT_MAX_BYTES, {
    message: `snapshot exceeds ${SNAPSHOT_MAX_BYTES} bytes`,
});
// `name` .max(60) matches saved_views.name varchar(60); CI-unique per
// (user, project, surface) — 23505 → SAVED_VIEW_NAME_EXISTS.
const NameSchema = z.string().trim().min(1).max(60);
// ── GET /api/projects/:projectId/saved-views?surface_key=… ──────────────────
export const ListSavedViewsQuerySchema = z
    .object({ surface_key: SavedViewSurfaceKeySchema })
    .strict();
// ── POST /api/projects/:projectId/saved-views ────────────────────────────────
// `position` is server-assigned (max+1 within the surface); `is_default` starts
// false — make-default is an explicit PATCH (the chevron action), never a create flag.
export const CreateSavedViewRequestSchema = z
    .object({
    surface_key: SavedViewSurfaceKeySchema,
    name: NameSchema,
    snapshot: SavedViewSnapshotSchema,
})
    .strict();
// ── PATCH /api/projects/:projectId/saved-views/:viewId ──────────────────────
// Partial update, at least one field: rename (`name`), update-in-place
// (`snapshot` — the "Update ⟨name⟩" path), make-default / clear-default
// (`is_default`). `surface_key` is immutable — a view never changes surface.
export const UpdateSavedViewRequestSchema = z
    .object({
    name: NameSchema.optional(),
    snapshot: SavedViewSnapshotSchema.optional(),
    is_default: z.boolean().optional(),
})
    .strict()
    .refine((b) => b.name !== undefined || b.snapshot !== undefined || b.is_default !== undefined, {
    message: 'at least one of name, snapshot, is_default',
});
// ── the surface-default store (user_surface_prefs — saved views' sibling) ─────
// The IMPLICIT last choice a surface remembers per person (the files library's
// grouping + sort per entity kind first: 'files:site' · 'files:asset' ·
// 'files:person'; the registers' display prefs are named adopters). One row per
// (person, surface), written by use, deleted by "Reset view"; `prefs` is opaque
// like the snapshot — the surface owns its keys. No project axis: a default
// follows the person across projects (a surface ever needing a per-project
// default adds a scoped key form then — additive).
export const PREFS_MAX_BYTES = 2 * 1024;
/** Distinct surface keys one person may hold — surfaces are a bounded set by
 *  design (dozens); the cap only fences a scripted writer from growing the
 *  table unboundedly (the boot read returns ALL of a person's rows). */
export const PREFS_MAX_KEYS = 100;
export const SurfacePrefsSchema = z
    .record(z.string().max(64), z.unknown())
    .refine((s) => JSON.stringify(s).length <= PREFS_MAX_BYTES, {
    message: `prefs exceed ${PREFS_MAX_BYTES} bytes`,
});
// PUT /api/auth/me/surface-prefs/:surfaceKey — upsert (the whole object, tiny).
export const PutSurfacePrefsRequestSchema = z.object({ prefs: SurfacePrefsSchema }).strict();
